import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";

function DeleteButton({ item, label, slug, onConfirm }) {
  const eq = item.equipments_count || 0;
  const os = item.orders_count || 0;
  const inUse = eq + os > 0;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm h-8 text-red-600 hover:text-red-700" data-testid={`delete-${slug}-${item.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`delete-${slug}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir {label.toLowerCase()} "{item.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            {inUse ? (
              <span className="block" data-testid={`delete-${slug}-warning`}>
                <b className="text-red-600">Atenção:</b> este item está em uso por {eq > 0 && <b>{eq} equipamento(s)</b>}{eq > 0 && os > 0 && " e "}{os > 0 && <b>{os} OS</b>}.
                Deseja realmente continuar? Os registros existentes serão <b>preservados</b> com o nome atual como histórico, mas ele não aparecerá mais nas opções de cadastro.
              </span>
            ) : (
              <span className="block">Este item não está em uso. Essa ação não pode ser desfeita.</span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(item)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-${slug}-${item.id}`}>
            {inUse ? "Sim, excluir mesmo assim" : "Excluir"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Row({ item, label, slug, endpoint, onChange }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const save = async () => {
    try {
      const { data } = await api.patch(`/${endpoint}/${item.id}`, { name });
      const n = (data.equipments_updated || 0) + (data.orders_updated || 0);
      toast.success(n > 0 ? `${label} renomeado(a). ${n} registro(s) atualizado(s).` : `${label} atualizado(a)`);
      setEditing(false);
      onChange();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };
  const remove = async () => {
    try {
      await api.delete(`/${endpoint}/${item.id}`);
      toast.success(`${label} excluído(a)`);
      onChange();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="flex items-center gap-2 px-5 py-3 border-b last:border-0" data-testid={`${slug}-row-${item.id}`}>
      {editing ? (
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="rounded-sm h-8 flex-1" data-testid={`${slug}-edit-input-${item.id}`} autoFocus />
      ) : (
        <span className="flex-1 font-medium" data-testid={`${slug}-name-${item.id}`}>{item.name}</span>
      )}
      {!editing && endpoint === "sectors" && <Badge variant="outline" className="rounded-sm">{item.equipments_count || 0} EQ</Badge>}
      {!editing && <Badge variant="outline" className="rounded-sm">{item.orders_count || 0} OS</Badge>}
      {editing ? (
        <>
          <Button size="sm" onClick={save} className="rounded-sm h-8 bg-orange-500 hover:bg-orange-600 text-white" data-testid={`${slug}-save-${item.id}`}><Check className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={() => { setEditing(false); setName(item.name); }} className="rounded-sm h-8"><X className="w-3.5 h-3.5" /></Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-sm h-8" data-testid={`edit-${slug}-${item.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
          <DeleteButton item={item} label={label} slug={slug} onConfirm={remove} />
        </>
      )}
    </div>
  );
}

export const TaxonomyManager = ({ title, label, slug, endpoint, items, onChange }) => {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return toast.error("Informe o nome");
    try {
      await api.post(`/${endpoint}`, { name });
      toast.success(`${label} cadastrado(a)`);
      setName("");
      onChange();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <Card className="rounded-sm shadow-none" data-testid={`${slug}-manager`}>
      <div className="p-5 border-b">
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Cadastro</div>
        <h3 className="font-heading text-xl font-bold">{title} <span className="text-muted-foreground text-sm font-normal">({items.length})</span></h3>
        <div className="flex gap-2 mt-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={`Novo(a) ${label.toLowerCase()}`} className="rounded-sm" data-testid={`${slug}-new-input`} />
          <Button onClick={add} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-1.5" data-testid={`${slug}-add-btn`}><Plus className="w-4 h-4" /> Adicionar</Button>
        </div>
      </div>
      {items.length === 0 && <div className="p-5 text-sm text-muted-foreground">Nenhum item cadastrado.</div>}
      {items.map((it) => <Row key={it.id} item={it} label={label} slug={slug} endpoint={endpoint} onChange={onChange} />)}
    </Card>
  );
};
